import React, { useEffect } from 'react'
import { useIntl } from 'react-intl'
import PropTypes from 'prop-types'
import {
  Actions,
  MENU_POSITION_LEFT,
  ButtonAction,
  TooltipUtils
} from 'mailup-web-components'
import {
  RCS_QUEUING_ERROR,
  RCS_SCHEDULED_READY,
  RCS_SENDING_NO_CREDIT,
  RCS_SENDING_READY,
  RCS_SENDING_RUNNING,
  RCS_QUEUING_RUNNING,
  RCS_SENDING_STOPPING,
  RCS_SENDING_STOPPED,
  RCS_SENDING_ERROR,
  RCS_SENDING_FAILED
} from 'Api/beRcsSend'
import { useRecoilCallback, useSetRecoilState } from 'recoil'
import { modalConfirmState, modalShedulingState } from '../recoil/states/page'
import {
  enqueueErrorRetryCallback,
  sendMessageCallback
} from '../recoil/callbacks/callbacks'

const ActionMenu = ({ item }) => {
  const intl = useIntl()
  const setModalConfirm = useSetRecoilState(modalConfirmState)
  const enqueueErrorRetry = useRecoilCallback(enqueueErrorRetryCallback, [])
  const setModalShedulingState = useSetRecoilState(modalShedulingState)
  const setMenuDisabled = val => {
    return val === RCS_QUEUING_RUNNING || val === RCS_SENDING_STOPPING
  }
  const sendMessage = useRecoilCallback(sendMessageCallback, [])

  useEffect(() => {
    setTimeout(() => {
      TooltipUtils.rebuild()
    })
  }, [])

  const actionDelete = () => {
    setModalConfirm(prev => ({
      ...prev,
      idSchedule: item.IdScheduledSending,
      idSending: item.IdSending,
      action: 'delete',
      modalConfirmShow: true,
      modalConfirmTitle: intl.formatMessage(
        {
          id: 'Load.Rcs.SendStatusModalDeleteTitle'
        },
        { X: item.MessageName }
      ),
      modalConfirmMessage: intl.formatMessage({
        id: 'Load.Rcs.SendStatusModalDeleteBody'
      }),
      modalCofirmActionString: intl.formatMessage({
        id: 'Load.Rcs.SendStatusModalDeleteBtn'
      }),
      modalConfirmCancelString: intl.formatMessage({
        id: 'Load.Rcs.SendStatusModalDeleteCancel'
      }),
      modalConfirmVariant: 'delete'
    }))
  }

  const actionCancelSchedule = () => {
    setModalConfirm(prev => ({
      ...prev,
      idSchedule: item.IdScheduledSending,
      idSending: item.IdSending,
      action: 'cancelSchedule',
      modalConfirmShow: true,
      modalConfirmTitle: intl.formatMessage(
        {
          id: 'Load.Rcs.SendStatusModalCancelScheduleTitle'
        },
        { X: item.MessageName }
      ),
      modalConfirmMessage: intl.formatMessage({
        id: 'Load.Rcs.SendStatusModalCancelScheduleBody'
      }),
      modalCofirmActionString: intl.formatMessage({
        id: 'Load.Rcs.SendStatusModalCancelScheduleConfirm'
      }),
      modalConfirmCancelString: intl.formatMessage({
        id: 'Load.Rcs.SendStatusModalCancelScheduleCancel'
      }),
      modalConfirmVariant: 'primary'
    }))
  }

  const actionSend = () => {
    sendMessage(item.IdScheduledSending, '', '')
  }

  const actionSchedule = () => {
    setModalShedulingState(prev => ({
      ...prev,
      show: true,
      isReschedule: false,
      idScheduledSending: item.IdScheduledSending
    }))
  }

  const actionReschedule = () => {
    setModalShedulingState(prev => ({
      ...prev,
      isReschedule: true,
      show: true,
      startDate: item.ScheduleDate,
      scheduleTimeZoneCode: item.UITimezone,
      idScheduledSending: item.IdScheduledSending
    }))
  }

  const queuingErrorActionRetry = idQueuing => {
    enqueueErrorRetry(idQueuing)
  }

  const actionStop = () => {
    setModalConfirm(prev => ({
      ...prev,
      idSchedule: item.IdScheduledSending,
      idSending: item.IdSending,
      action: 'stop',
      modalConfirmShow: true,
      modalConfirmTitle: intl.formatMessage({
        id: 'Load.Rcs.SendStatusModalStopSendingTitle'
      }),
      modalConfirmMessage: intl.formatMessage(
        {
          id: 'Load.Rcs.SendStatusModalStopSendingBody'
        },
        { strong: chunks => <strong>{chunks}</strong> }
      ),
      modalCofirmActionString: intl.formatMessage({
        id: 'Load.Rcs.SendStatusModalStopSendingBtnConfirm'
      }),
      modalConfirmCancelString: intl.formatMessage({
        id: 'Load.Rcs.SendStatusModalStopSendingBtnCancel'
      }),
      modalConfirmVariant: 'primary'
    }))
  }

  const actionActionResumeSending = () => {
    sendMessage(item.IdScheduledSending, '', '')
  }

  const setItems = val => {
    const items = []

    if (val === RCS_SCHEDULED_READY) {
      items.push({
        label: intl.formatMessage({
          id: 'Load.Rcs.SendStatusActionReschedule'
        }),
        onClick: actionReschedule,
        'data-mu-test': 'status-scheduled-ready_ActionReschedule'
      })
      items.push({
        label: intl.formatMessage({
          id: 'Load.Rcs.SendStatusActionCancelSchedule'
        }),
        onClick: actionCancelSchedule,
        'data-mu-test': 'status-scheduled-ready_ActionCancelSchedule'
      })
    }
    if (val === RCS_SENDING_READY) {
      items.push({
        label: intl.formatMessage({
          id: 'Load.Rcs.SendStatusActionSend'
        }),
        onClick: actionSend,
        'data-mu-test': 'status-sending-ready_ActionSend'
      })
      items.push({
        label: intl.formatMessage({
          id: 'Load.Rcs.SendStatusActionSchedule'
        }),
        onClick: actionSchedule,
        'data-mu-test': 'status-sending-ready_ActionSchedule'
      })
    }
    if (val === RCS_QUEUING_ERROR) {
      items.push({
        label: intl.formatMessage({
          id: 'Load.Rcs.SendStatusActionRetry'
        }),
        onClick: () => queuingErrorActionRetry(item.IdQueueConf),
        'data-mu-test': 'status-queuing-error_ActionRetry'
      })
    }

    if (val === RCS_SENDING_NO_CREDIT) {
      items.push({
        label: intl.formatMessage({
          id: 'Load.Rcs.SendStatusActionRetry'
        }),
        onClick: actionSend,
        'data-mu-test': 'status-no-credit_ActionRetry'
      })
      if (item.Type === 'Immediate') {
        items.push({
          label: intl.formatMessage({
            id: 'Load.Rcs.SendStatusActionSchedule'
          }),
          onClick: actionSchedule,
          'data-mu-test': 'status-no-credit_ActionSchedule'
        })
      }
      if (item.Type === 'Scheduled') {
        items.push({
          label: intl.formatMessage({
            id: 'Load.Rcs.SendStatusActionReschedule'
          }),
          onClick: actionReschedule,
          'data-mu-test': 'status-no-credit_ActionReschedule'
        })
      }
    }

    if (val === RCS_SENDING_STOPPED) {
      items.push({
        label: intl.formatMessage({
          id: 'Load.Rcs.SendStatusActionResumeSending'
        }),
        onClick: actionActionResumeSending,
        'data-mu-test': 'status-stop_ActionResumeSending'
      })
      items.push({
        label: intl.formatMessage({
          id: 'Load.Rcs.SendStatusActionReschedule'
        }),
        onClick: actionReschedule,
        'data-mu-test': 'status-stop_ActionReschedule'
      })
    }

    items.push({
      label: intl.formatMessage({
        id: 'Load.Rcs.SendStatusActionDelete'
      }),
      onClick: actionDelete,
      'data-mu-test': 'ActionDelete'
    })
    return items
  }

  return (
    <>
      {item.State === RCS_SENDING_RUNNING && (
        <div
          data-tip={intl.formatMessage({
            id: 'Load.Rcs.SendStatusActionStopTooltip'
          })}
        >
          <ButtonAction
            data-mu-test='ActionStop'
            type='pause'
            onClick={actionStop}
          />
        </div>
      )}
      {(item.State === RCS_SENDING_ERROR ||
        item.State === RCS_SENDING_FAILED) && (
        <div
          data-tip={intl.formatMessage({
            id: 'Load.Rcs.SendStatusActionDeleteTooltip'
          })}
        >
          <ButtonAction
            data-mu-test='ActioDelete'
            type='delete'
            onClick={actionDelete}
          />
        </div>
      )}
      {item.State !== RCS_SENDING_RUNNING &&
        item.State !== RCS_SENDING_ERROR &&
        item.State !== RCS_SENDING_FAILED && (
          <Actions
            disabled={setMenuDisabled(item.State)}
            items={setItems(item.State)}
            menuPosition={MENU_POSITION_LEFT}
          />
        )}
    </>
  )
}

export default ActionMenu

ActionMenu.propTypes = {
  item: PropTypes.any
}
